import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  useBalancete, useDre, useBalanco,
} from "@/features/contabil/hooks/useContabilizacao";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

export default function RelatoriosContabeisPage() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;
  const [dtInicio, setDtInicio] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [dtFim, setDtFim] = useState(new Date().toISOString().slice(0, 10));
  const [dtPosicao, setDtPosicao] = useState(new Date().toISOString().slice(0, 10));

  const balancete = useBalancete(empresaId, dtInicio, dtFim);
  const dre = useDre(empresaId, dtInicio, dtFim);
  const balanco = useBalanco(empresaId, dtPosicao);

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Relatórios Contábeis</h1>
        <p className="text-muted-foreground">DRE, Balancete e Balanço Patrimonial.</p>
      </div>

      <Tabs defaultValue="dre" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dre">DRE</TabsTrigger>
          <TabsTrigger value="balancete">Balancete</TabsTrigger>
          <TabsTrigger value="balanco">Balanço Patrimonial</TabsTrigger>
        </TabsList>

        <TabsContent value="dre" className="space-y-4">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>De</Label>
                <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
              </div>
              <div>
                <Label>Até</Label>
                <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {dre.isLoading ? <Skeleton className="h-48" /> : !dre.data ? null : (
            <Card>
              <CardHeader>
                <CardTitle>DRE — {new Date(dre.data.dtInicio).toLocaleDateString("pt-BR")} a {new Date(dre.data.dtFim).toLocaleDateString("pt-BR")}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Receitas: <strong className="text-green-600">{fmtCur(dre.data.receitas)}</strong> ·
                  Despesas: <strong className="text-red-600">{fmtCur(dre.data.despesas)}</strong> ·
                  Resultado: <strong className={dre.data.resultadoPeriodo >= 0 ? "text-green-600" : "text-red-600"}>{fmtCur(dre.data.resultadoPeriodo)}</strong>
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conta</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dre.data.linhas.map((l) => (
                      <TableRow key={l.contaId}>
                        <TableCell className="font-mono text-sm">{l.codigoConta}</TableCell>
                        <TableCell>{l.descricaoConta}</TableCell>
                        <TableCell>{l.isReceita ? "Receita" : "Despesa"}</TableCell>
                        <TableCell className={`text-right font-semibold ${l.isReceita ? "text-green-600" : "text-red-600"}`}>
                          {fmtCur(l.valor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="balancete" className="space-y-4">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>De</Label>
                <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} />
              </div>
              <div>
                <Label>Até</Label>
                <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {balancete.isLoading ? <Skeleton className="h-48" /> : !balancete.data ? null : (
            <Card>
              <CardHeader>
                <CardTitle>Balancete — {balancete.data.contas.length} contas</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Total D: <strong>{fmtCur(balancete.data.totalDebitos)}</strong> ·
                  Total C: <strong>{fmtCur(balancete.data.totalCreditos)}</strong>
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conta</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-right">Saldo anterior</TableHead>
                      <TableHead className="text-right">Débitos</TableHead>
                      <TableHead className="text-right">Créditos</TableHead>
                      <TableHead className="text-right">Saldo atual</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {balancete.data.contas.map((c) => (
                      <TableRow key={c.contaId}>
                        <TableCell className="font-mono text-sm">{c.codigoConta}</TableCell>
                        <TableCell>{c.descricaoConta}</TableCell>
                        <TableCell className="text-right">{fmtCur(c.saldoAnterior)}</TableCell>
                        <TableCell className="text-right">{fmtCur(c.debitos)}</TableCell>
                        <TableCell className="text-right">{fmtCur(c.creditos)}</TableCell>
                        <TableCell className={`text-right font-semibold ${c.saldoAtual >= 0 ? "" : "text-red-600"}`}>
                          {fmtCur(c.saldoAtual)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="balanco" className="space-y-4">
          <Card>
            <CardContent className="pt-6">
              <div className="max-w-xs">
                <Label>Data da posição</Label>
                <Input type="date" value={dtPosicao} onChange={(e) => setDtPosicao(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {balanco.isLoading ? <Skeleton className="h-48" /> : !balanco.data ? null : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Ativo</CardTitle>
                  <p className="text-sm text-muted-foreground">Total: <strong>{fmtCur(balanco.data.totalAtivo)}</strong></p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Conta</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {balanco.data.ativo.map((l) => (
                        <TableRow key={l.contaId}>
                          <TableCell className="text-sm">{l.codigoConta} — {l.descricaoConta}</TableCell>
                          <TableCell className="text-right">{fmtCur(l.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Passivo + PL</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Passivo: <strong>{fmtCur(balanco.data.totalPassivo)}</strong> ·
                    PL: <strong>{fmtCur(balanco.data.totalPatrimonioLiquido)}</strong>
                  </p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Conta</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {[...balanco.data.passivo, ...balanco.data.patrimonioLiquido].map((l) => (
                        <TableRow key={l.contaId}>
                          <TableCell className="text-sm">{l.codigoConta} — {l.descricaoConta}</TableCell>
                          <TableCell className="text-right">{fmtCur(l.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
